import * as Msal from '@azure/msal-browser';

const msalConfig = {
  auth: {
    clientId: 'e53b162d-f309-4f9f-a108-49a7fdf95f6e', //This is your client ID
    authority: "https://login.microsoftonline.com/c4484a25-d0d8-4ad0-916a-a889b6aa7a02", //This is your tenant info
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true
  }
};

export const loginScopes = {
  scopes: ["api://44712413-cec5-405e-959e-798e50e6bcf6/Data.All", "User.Read", "Directory.Read.All"]
};

const WINTRAK_SCOPE = "api://44712413-cec5-405e-959e-798e50e6bcf6/Data.All";
const DIRECTORY_SCOPE = "Directory.Read.All";

const myMSALObj = new Msal.PublicClientApplication(msalConfig);
export default myMSALObj;

function setActiveAccountIfNeeded(account) {
  if (account && typeof myMSALObj.setActiveAccount === "function") {
    myMSALObj.setActiveAccount(account);
  }
}

function getPreferredAccount() {
  const activeAccount =
    typeof myMSALObj.getActiveAccount === "function"
      ? myMSALObj.getActiveAccount()
      : null;
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = myMSALObj.getAllAccounts();
  if (accounts.length > 0) {
    setActiveAccountIfNeeded(accounts[0]);
    return accounts[0];
  }

  return null;
}

function isInteractionRequiredError(error) {
  if (!error) {
    return false;
  }

  if (
    Msal.InteractionRequiredAuthError &&
    error instanceof Msal.InteractionRequiredAuthError
  ) {
    return true;
  }

  const interactionRequiredErrorCodes = [
    "consent_required",
    "interaction_required",
    "login_required",
    "no_tokens_found",
    "token_refresh_required",
    "monitor_window_timeout",
    "invalid_grant",
  ];

  return interactionRequiredErrorCodes.includes(error.errorCode);
}

function requiresInteraction(errorCode) {
  if (!errorCode || !errorCode.length) {
    return false;
  }
  return (
    errorCode === "consent_required" ||
    errorCode === "interaction_required" ||
    errorCode === "login_required"
  );
}

async function acquireTokenInternal(scopes) {
  const requestedScopes = scopes || [WINTRAK_SCOPE];
  let account = getPreferredAccount();

  if (!account) {
    return null;
  }

  const requestWithAccount = { scopes: requestedScopes, account };

  try {
    const tokenResponse = await myMSALObj.acquireTokenSilent(requestWithAccount);
    return tokenResponse.accessToken;
  } catch (error) {
    console.error(error);

    if (isInteractionRequiredError(error) || requiresInteraction(error.errorCode)) {
      return null;
    }

    return null;
  }
}

// Deprecated, older function for wrapping token refresh logic for use
// when calling an API. The function fetchWithToken may be easier to use.
export async function acquireTokenPopupAndCallAPI(apiCallWithTokenFnAsync) {
  try {
    let token = await acquireTokenInternal([WINTRAK_SCOPE]);

    if (!token) {
      console.error(new Error("Unable to acquire access token."));
      return null;
    }

    let response = await apiCallWithTokenFnAsync(token);

    // Retry once after re-authentication when backend returns unauthorized.
    if (response && (response.status === 401 || response.status === 403)) {
      token = await acquireTokenInternal([WINTRAK_SCOPE]);

      if (!token) {
        console.error(new Error("Unable to refresh access token after re-authentication."));
        return null;
      }

      response = await apiCallWithTokenFnAsync(token);
    }

    return response;
  } catch (error) {
    console.error(error);
    return null;
  }
}

/**
 * Acquires a bearer token using MSAL.
 * @returns {Promise<string|null>} A Promise with the token or null if
 * the token could not be obtained.
 */
export async function acquireToken(scopes) {
  return acquireTokenInternal(scopes || [WINTRAK_SCOPE]);
}

/**
 * Acquires a bearer token using MSAL and makes a Fetch API call using the token.
 * @param input The Request object.
 * @param init
 * @returns {Promise<Response>} A Promise with the Response object from the Fetch API.
 */
export async function fetchWithToken(input, init) {
  const token = await acquireToken();
  if (!token) {
    throw new Error("Unable to acquire access token.");
  }
  const request = new Request(input, init);
  request.headers.append('Authorization', `Bearer ${token}`);
  return fetch(request);
}

// Same purpose as fetchWithToken, but specifically for use with MS Graph APIs for reading
// Azure AD information
export async function fetchGraphWithToken(input, init) {
  const token = await acquireToken([DIRECTORY_SCOPE]);
  if (!token) {
    throw new Error("Unable to acquire directory access token.");
  }
  const request = new Request(input, init);
  request.headers.append('Authorization', `Bearer ${token}`);
  return fetch(request);
}
